// Read Aloud's decisions that don't need a page: splitting text into sentences, choosing voices,
// and the "where I stopped" list. app/read-aloud.js does the page part. Tested in
// test/read-aloud.test.mjs.

// Sentences with their offsets in the text. Intl.Segmenter when the browser has it, otherwise a
// punctuation rule that keeps "e.g." and "Dr." together.
const ABBR = /\b(?:e\.g|i\.e|etc|vs|mr|mrs|ms|dr|st|no|fig|p|pp|approx|ca|jr|sr)\.$/i;
export function splitSentences(text, lang = "en") {
	const out = [];
	const push = (start, end) => {
		while (start < end && /\s/.test(text[start])) start++;
		while (end > start && /\s/.test(text[end - 1])) end--;
		if (end > start && /[\p{L}\p{N}]/u.test(text.slice(start, end))) out.push({ start, end, text: text.slice(start, end) });
	};
	if (typeof Intl !== "undefined" && Intl.Segmenter) {
		let seg;
		try { seg = new Intl.Segmenter(lang, { granularity: "sentence" }); } catch { seg = new Intl.Segmenter("en", { granularity: "sentence" }); }
		let carry = -1;
		for (const s of seg.segment(text)) {
			const start = carry >= 0 ? carry : s.index;
			const end = s.index + s.segment.length;
			if (ABBR.test(s.segment.trimEnd()) && end < text.length) { carry = start; continue; }
			carry = -1;
			push(start, end);
		}
		if (carry >= 0) push(carry, text.length);
	} else {
		const re = /[.!?…]+["'”’)\]]*(?=\s+|$)/g;
		let start = 0, m;
		while ((m = re.exec(text))) {
			const end = m.index + m[0].length;
			if (ABBR.test(text.slice(start, end))) continue;
			push(start, end);
			start = end;
		}
		push(start, text.length);
	}
	// Very long "sentences" (lists without full stops) are split again so no one utterance runs on.
	const final = [];
	for (const s of out) {
		if (s.end - s.start <= 400) { final.push(s); continue; }
		let a = s.start;
		while (a < s.end) {
			let b = Math.min(s.end, a + 300);
			if (b < s.end) {
				// break after a comma or semicolon where there is one, so the pause lands in a natural place
				const part = text.slice(a, b);
				const m = Math.max(part.lastIndexOf(", "), part.lastIndexOf("; "), part.lastIndexOf(": "));
				const cut = m > 120 ? a + m + 1 : text.lastIndexOf(" ", b);
				if (cut > a + 100) b = cut;
			}
			final.push({ start: a, end: b, text: text.slice(a, b).trim() });
			a = b;
			while (a < s.end && /\s/.test(text[a])) a++;
		}
	}
	return final;
}

// Voices, best first: on this device before online, the page's language before the browser's,
// then the rest; "Natural", "Neural", "Enhanced" and "Premium" ones first within each. Online voices
// (they send the text to a speech service) are left out unless allowed.
const NICE = /natural|neural|enhanced|premium/i;
export function voiceList(voices, { lang = "en", online = false } = {}) {
	const want = String(lang || "en").toLowerCase();
	const base = want.split("-")[0];
	const score = (v) => {
		const l = String(v.lang || "").toLowerCase().replace("_", "-");
		let s = 0;
		if (v.localService) s += 100;
		if (l === want) s += 40;
		else if (l.split("-")[0] === base) s += 30;
		if (NICE.test(v.name)) s += 10;
		if (v.default) s += 1;
		return s;
	};
	return [...(voices || [])].filter((v) => v && (v.localService || online)).sort((a, b) => score(b) - score(a) || String(a.name).localeCompare(String(b.name)));
}
// The voice to use: the saved one if it is still there (and allowed), else the best match for the
// page's language. null means no allowed voice speaks that language.
export function pickVoice(voices, { name = "", lang = "en", online = false } = {}) {
	const list = voiceList(voices, { lang, online });
	if (name) { const v = list.find((x) => x.name === name); if (v) return v; }
	const base = String(lang || "en").toLowerCase().split("-")[0];
	return list.find((v) => String(v.lang || "").toLowerCase().split(/[-_]/)[0] === base) || null;
}

// --- where I stopped -----------------------------------------------------------------------------
export const POS_KEY = "revault:read-pos";
export const POS_MAX = 50;
export const POS_DAYS = 30;
// A short hash of the text, so a page whose words changed starts over.
export function textHash(text) {
	let h = 2166136261;
	const t = String(text || "");
	for (let i = 0; i < t.length; i += 7) { h ^= t.charCodeAt(i); h = Math.imul(h, 16777619); }
	return (h >>> 0).toString(36) + "." + t.length.toString(36);
}
export const posKey = (url, text) => String(url || "").split("#")[0].slice(0, 300) + "|" + textHash(text);
// Allowed only with history on, out of incognito, and with the setting on.
export const mayRemember = (get) => get("readRemember") !== false && get("incognito") !== true && get("keepHistory") !== false;
export function posGet(list, key, now = Date.now()) {
	const e = list && typeof list === "object" ? list[key] : null;
	return e && typeof e.i === "number" && now - (e.at || 0) < POS_DAYS * 864e5 ? e.i : -1;
}
// Returns the new list: newest first, at most POS_MAX, nothing older than POS_DAYS.
export function posPut(list, key, i, now = Date.now()) {
	const all = Object.entries(list && typeof list === "object" ? list : {}).filter(([k, e]) => k !== key && e && now - (e.at || 0) < POS_DAYS * 864e5);
	if (i >= 0) all.unshift([key, { i, at: now }]);
	all.sort((a, b) => b[1].at - a[1].at);
	return Object.fromEntries(all.slice(0, POS_MAX));
}

// Reading speeds on the menu.
export const RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];
export const nextRate = (r, dir) => { const i = RATES.indexOf(Number(r)); const j = Math.max(0, Math.min(RATES.length - 1, (i < 0 ? 1 : i) + dir)); return RATES[j]; };
